import type { Tour } from '../tour-state.js';

// The Workflows page's own tour, on the first visit once the welcome tour is done: the list, then one
// workflow opened and its overview explained part by part, then its other tabs. With no workflow in
// progress, only the list is explained.
const hasWorkflow = (context: { hasWorkflow?: boolean }) => !!context.hasWorkflow;

// Opening the first workflow and the steps that point inside it; closing it (its collapse button or
// the browser's Back) sends the tour back to opening it
export const WORKFLOW_STEPS = [
  'open-workflow',
  'workflow-header',
  'workflow-counts',
  'workflow-ledger',
  'workflow-current',
  'workflow-progress',
  'workflow-subworkflows',
  'workflow-jobs-tab',
  'workflow-job-status',
  'workflow-job-holder',
  'workflow-collapse',
];

const inside = (step: Omit<Tour['steps'][number], 'advance'>): Tour['steps'][number] => ({
  advance: 'next',
  passive: true,
  when: hasWorkflow,
  group: 'opened-workflow',
  ...step,
});

const workflows: Tour = {
  id: 'workflows',
  steps: [
    {
      id: 'workflow-list',
      targets: ['workflow-feed'],
      text: "These are your company's workflows in progress. Each card is a process running for you, such as onboarding a supplier.",
      advance: 'next',
      passive: true,
    },
    {
      id: 'open-workflow',
      targets: ['first-workflow'],
      text: 'Click a workflow to open it.',
      advance: 'next',
      onNext: 'open-first-workflow',
      doneOn: ['workflow-opened'],
      when: hasWorkflow,
    },
    inside({
      id: 'workflow-header',
      targets: ['workflow-header'],
      text: "The workflow's name and what it's for. The tabs switch between its overview, its sub-workflows and its jobs.",
    }),
    inside({
      id: 'workflow-counts',
      targets: ['workflow-counts'],
      text: 'How many of its jobs are done and how many are still open.',
    }),
    inside({
      id: 'workflow-ledger',
      targets: ['workflow-ledger'],
      text: 'What this workflow costs on the public ledger, in total and from where it is now.',
      optional: true,
    }),
    inside({
      id: 'workflow-current',
      targets: ['workflow-current'],
      text: "Where the workflow is now and what it's waiting for.",
    }),
    inside({
      id: 'workflow-progress',
      targets: ['workflow-progress'],
      text: 'When it started, what started it and who it has invited.',
    }),
    inside({
      // The tab shows only when the workflow has started others
      id: 'workflow-subworkflows',
      select: 'workflow-tab-workflows',
      targets: ['workflow-subworkflows'],
      text: 'The workflows this one has started, and how each is going.',
      optional: true,
    }),
    // The Jobs tab is a view of its own: whether it has rows is only known once it's open, so its
    // group is checked there, and the rows are known as soon as the table (or "No jobs yet") shows
    inside({
      id: 'workflow-jobs-tab',
      select: 'workflow-tab-jobs',
      group: 'workflow-jobs',
      targets: ['workflow-jobs-tab'],
      text: 'The Jobs tab lists every job this workflow has created, and when. Hover over a title to read it in full.',
    }),
    inside({
      // A workflow with no jobs yet shows an empty tab, so the row steps are left out
      id: 'workflow-job-status',
      select: 'workflow-tab-jobs',
      group: 'workflow-jobs',
      decidedBy: 'workflow-jobs-tab',
      targets: ['workflow-job-status'],
      text: "Each job's status: Open while it's being worked on, then Completed or Cancelled.",
      optional: true,
    }),
    inside({
      id: 'workflow-job-holder',
      select: 'workflow-tab-jobs',
      group: 'workflow-jobs',
      decidedBy: 'workflow-jobs-tab',
      targets: ['workflow-job-holder'],
      text: "Who has the job. Unclaimed means it's waiting for someone in its team to pick it up.",
      optional: true,
    }),
    inside({
      id: 'workflow-collapse',
      select: 'workflow-tab-overview',
      targets: ['workflow-collapse'],
      text: 'Close the workflow here to go back to the list.',
    }),
  ],
};

export default workflows;
